import { onDocumentReady, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';

const REVEAL_SELECTOR = '[data-scroll-reveal]';
const ITEM_SELECTOR = '.text-block, .image-block, .hero__media-grid > *';
const MAX_STAGGER_INDEX = 6;

/**
 * Containers whose contents should never be tagged for scroll-reveal: repeating
 * grids, carousels, and interactive chrome where a reveal animation would feel
 * gimmicky or fight with existing hover/quick-add interactions.
 */
const EXCLUDED_ANCESTOR_SELECTOR = [
  '.product-grid',
  '.product-card',
  '.collection-list',
  'slideshow-component',
  'quick-add-component',
  'cart-drawer-component',
  'search-modal-component',
  '#header-group',
  'header-component',
  '[data-testid="footer-utilities"]',
].join(', ');

/**
 * Case-insensitive substrings matched against a section's id attribute.
 * Sections whose id contains one of these are skipped entirely, regardless of
 * what content they contain.
 * - newreleases: "New Releases" grid — heading faded in ahead of the (instant)
 *   product cards beneath it, which read as a loading glitch rather than polish.
 */
const EXCLUDED_SECTION_ID_SUBSTRINGS = ['newreleases'];

/**
 * Tags an element for scroll-reveal and records its stagger position within its section.
 * @param {HTMLElement} element
 * @param {'section' | 'item'} kind
 * @param {number} order
 */
function tag(element, kind, order) {
  element.setAttribute('data-scroll-reveal', kind);
  element.style.setProperty('--scroll-reveal-order', String(Math.min(order, MAX_STAGGER_INDEX)));
}

/**
 * Finds and tags the elements to reveal within a single Shopify section.
 * Sections with no recognized content blocks (custom or legacy markup) still
 * get one gentle reveal so nothing on the page is left un-animated.
 * @param {Element} section
 */
function tagSection(section) {
  // Check both ancestors AND descendants: a section can itself sit outside any
  // excluded container while still *containing* one (e.g. the cart drawer's
  // .shopify-section wrapper holds <cart-drawer-component> as a child, not a
  // parent). Missing the descendant case let the whole wrapper fall through to
  // the "no matching content" fallback below and get tagged for reveal — since
  // a closed drawer never scrolls into view, it stayed at opacity: 0 forever.
  //
  // The footer's "Policies and links" section falls into the same trap for a
  // different reason: none of its blocks use .text-block/.image-block, so it
  // gets the whole-section fallback below, and since it's the last, short
  // (~65px) element on the page, the observer's rootMargin (-10% of viewport
  // height, e.g. ~90px) can never contain any part of it — .is-revealed never
  // fires and it stays invisible forever. Excluded here for the same reason
  // the cart drawer is: it's always-relevant chrome, not content to animate in.
  if (section.closest(EXCLUDED_ANCESTOR_SELECTOR) || section.querySelector(EXCLUDED_ANCESTOR_SELECTOR)) return;

  const sectionId = section.id.toLowerCase();
  if (EXCLUDED_SECTION_ID_SUBSTRINGS.some((substring) => sectionId.includes(substring))) return;

  let order = 0;
  let tagged = false;

  section.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
    if (!(item instanceof HTMLElement) || item.closest(EXCLUDED_ANCESTOR_SELECTOR)) return;

    tag(item, 'item', order);
    order += 1;
    tagged = true;
  });

  if (!tagged && section instanceof HTMLElement) {
    tag(section, 'section', 0);
  }
}

function observe() {
  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        obs.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0 }
  );

  document.querySelectorAll(REVEAL_SELECTOR).forEach((element) => observer.observe(element));
}

function init() {
  if (prefersReducedMotion()) return;

  document.querySelectorAll('.shopify-section').forEach(tagSection);
  document.documentElement.classList.add('scroll-reveal-ready');

  observe();
}

onDocumentReady(() => {
  requestIdleCallback(init);
});
